import { computed, watchEffect } from 'vue'
import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'
import type { EmbedEditorScene, FeatureKey, FeatureState, MenuFeatureKey, RightPanelFeatureKey, ToolbarFeatureKey } from '../types/preset'
import { useContextStore } from 'client/store/context-store'
import { getEmbedEditorPreset } from '../presets'

export const usePresetConfig = defineStore('embedEditorPresetConfig', () => {
  const route = useRoute()
  const contextStore = useContextStore()

  const currentScene = computed<EmbedEditorScene>(() => {
    const path = route?.path ?? ''
    if (path.includes('/maker')) return 'maker'
    return 'default'
  })

  watchEffect(() => {
    contextStore.setContext('isMakerEmbedEditor', currentScene.value === 'maker')
  })

  // current preset config（read-only, not allowed to be modified externally）
  const preset = computed(() => {
    return getEmbedEditorPreset(currentScene.value)
  })

  const getFeatureState = (feature: FeatureKey): FeatureState => {
    const p = preset.value
    if (feature in p.menuFeatures) return p.menuFeatures[feature as MenuFeatureKey]
    if (feature in p.toolbarFeatures) return p.toolbarFeatures[feature as ToolbarFeatureKey]
    if (feature in p.rightPanelFeatures) return p.rightPanelFeatures[feature as RightPanelFeatureKey]
    return 'disabled'
  }

  const isFeatureEnabled = (feature: FeatureKey): boolean => {
    return getFeatureState(feature) === 'enabled'
  }

  const isFeatureLocked = (feature: FeatureKey): boolean => {
    return getFeatureState(feature) === 'locked'
  }

  const isFeatureDisabled = (feature: FeatureKey): boolean => {
    return getFeatureState(feature) === 'disabled'
  }

  // feature available（enabled or locked are available, just locked needs to be logged in）
  const isFeatureAvailable = (feature: FeatureKey): boolean => {
    return getFeatureState(feature) !== 'disabled'
  }

  const isColorThemeAllowed = (themeName: string): boolean => {
    if (preset.value.allowedColorThemes === 'all') return true
    return preset.value.allowedColorThemes.includes(themeName)
  }

  const isColorThemeLocked = (themeName: string): boolean => {
    if (preset.value.allowedColorThemes === 'all') return false
    return !preset.value.allowedColorThemes.includes(themeName)
  }

  const isSkeletonAllowed = (skeletonId: string): boolean => {
    if (preset.value.allowedSkeletons === 'all') return true
    return preset.value.allowedSkeletons.some(allowed => skeletonId.includes(allowed))
  }

  return {
    currentScene,
    preset,
    isFeatureEnabled,
    isFeatureLocked,
    isFeatureDisabled,
    isFeatureAvailable,
    isColorThemeAllowed,
    isColorThemeLocked,
    isSkeletonAllowed,
  }
})
